import React, { useEffect, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Container,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Chip,
    Alert,
    Grid,
    useMediaQuery,
    Card,
    CardContent,
    Stack,
    Divider,
} from '@mui/material';
import { Print } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchClients } from '../../store/slices/clientSlice';
import { consignmentPricingApi, ConsignmentPricingItem, PricingData } from '../../services/consignmentPricingApi';
import { useTheme } from '../../contexts/ThemeContext';
import ConsignmentView from '../Consignments/ConsignmentView';
import { getAuthToken, API_BASE_URL } from '../../services/apiUtils';

const ConsignmentPricing: React.FC = () => {
    const { theme } = useTheme();
    const dispatch = useAppDispatch();
    const { user } = useAppSelector((state) => state.auth);
    const { clients, loading: clientsLoading } = useAppSelector((state) => state.clients);
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pricingData, setPricingData] = useState<PricingData | null>(null);
    const [selectedCustomerId, setSelectedCustomerId] = useState<number | undefined>(undefined);
    const [consignmentModalOpen, setConsignmentModalOpen] = useState(false);
    const [selectedConsignmentId, setSelectedConsignmentId] = useState<number | null>(null);

    // Default date range: Last 1 year
    const today = new Date();
    const lastYear = new Date();
    lastYear.setFullYear(today.getFullYear() - 1);

    const [fromDate, setFromDate] = useState<string>(lastYear.toISOString().split('T')[0]);
    const [toDate, setToDate] = useState<string>(today.toISOString().split('T')[0]);
    const [customerOptions, setCustomerOptions] = useState<Array<{ id: number; name: string }>>([]);

    const isClient = user?.type === 'CLIENT';
    const isAdminOrManager = user?.type === 'ADMIN' || user?.type === 'MANAGER';

    useEffect(() => {
        if (isAdminOrManager) {
            dispatch(fetchClients({ per_page: 1000 }));
        }
    }, [dispatch, isAdminOrManager]);

    useEffect(() => {
        if (clients) {
            setCustomerOptions([
                { id: -1, name: 'All Customers' },
                ...clients.map((c) => ({ id: c.id, name: `${c.name}${c.client_profile?.company_name ? ` (${c.client_profile.company_name})` : ''}` }))
            ]);
        }
    }, [clients]);

    useEffect(() => {
        if (isClient && user?.id) {
            setSelectedCustomerId(user.id);
            fetchPricingData(user.id, fromDate, toDate);
        } else if (isAdminOrManager) {
            // Fetch default data for admins/managers (All customers, last 1 year)
            // Set selectedCustomerId to -1 (All Customers) for UI consistency, but pass undefined to API
            setSelectedCustomerId(-1);
            fetchPricingData(undefined, fromDate, toDate);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isClient, user, isAdminOrManager]);

    const fetchPricingData = async (customerId?: number, from?: string, to?: string) => {
        setLoading(true);
        setError(null);
        try {
            const params: any = {};
            if (customerId) params.customer_id = customerId;
            if (from) params.FromDate = from;
            if (to) params.ToDate = to;

            const data = await consignmentPricingApi.getPricingData(params);
            setPricingData(data);
        } catch (err: any) {
            setError(err.message || 'Failed to fetch pricing data');
        } finally {
            setLoading(false);
        }
    };

    const handleCustomerChange = (_: any, newValue: { id: number; name: string } | null) => {
        const customerId = newValue?.id;
        setSelectedCustomerId(customerId);

        // If "All Customers" (id: -1) or null is selected, pass undefined to API
        const apiCustomerId = (customerId === -1 || !customerId) ? undefined : customerId;
        fetchPricingData(apiCustomerId, fromDate, toDate);
    };

    const handleApplyFilters = () => {
        const apiCustomerId = (selectedCustomerId === -1 || !selectedCustomerId) ? undefined : selectedCustomerId;
        fetchPricingData(apiCustomerId, fromDate, toDate);
    };

    const handlePrint = () => {
        const token = getAuthToken();
        const apiCustomerId = (selectedCustomerId === -1 || !selectedCustomerId) ? undefined : selectedCustomerId;

        const params = new URLSearchParams();
        if (token) params.append('token', token);
        if (apiCustomerId) params.append('customer_id', apiCustomerId.toString());
        if (fromDate) params.append('FromDate', fromDate);
        if (toDate) params.append('ToDate', toDate);

        const url = `${API_BASE_URL}/public/consignments/pricing/print?${params.toString()}`;
        window.open(url, '_blank');
    };

    const handleConsignmentClick = (e: React.MouseEvent, consignmentId: number) => {
        e.preventDefault();
        setSelectedConsignmentId(consignmentId);
        setConsignmentModalOpen(true);
    };

    const getStatusChip = (statusId: number) => {
        return statusId === 26 ? (
            <Chip label="Paid" color="success" size="small" />
        ) : (
            <Chip label="Unpaid" color="default" size="small" />
        );
    };

    const calculateTotals = () => {
        if (!pricingData?.consignments) return { cod: 0, collected: 0, serviceCharges: 0, tax: 0 };

        return pricingData.consignments.reduce(
            (acc, item) => ({
                cod: acc.cod + (item.cod_amount || 0),
                collected: acc.collected + (item.pricing?.cod_amount || 0),
                serviceCharges: acc.serviceCharges + (item.pricing?.service_charges || 0),
                tax: acc.tax + (item.pricing?.tax || 0),
            }),
            { cod: 0, collected: 0, serviceCharges: 0, tax: 0 }
        );
    };

    const totals = calculateTotals();

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" gutterBottom>
                    Consignment Pricing
                </Typography>

                {/* Filters */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Filters
                    </Typography>
                    <Grid container spacing={2} alignItems="center">
                        {!isClient && (
                            <Grid size={{ xs: 12, md: 3 }}>
                                <Autocomplete
                                    options={customerOptions}
                                    getOptionLabel={(option) => option.name}
                                    loading={clientsLoading}
                                    onChange={handleCustomerChange}
                                    value={customerOptions.find(opt => opt.id === selectedCustomerId) || null}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Select Customer"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <React.Fragment>
                                                        {clientsLoading ? (
                                                            <CircularProgress color="inherit" size={20} />
                                                        ) : null}
                                                        {params.InputProps.endAdornment}
                                                    </React.Fragment>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                        )}

                        <Grid size={{ xs: 12, md: isClient ? 4 : 3 }}>
                            <TextField
                                label="From Date"
                                type="date"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: isClient ? 4 : 3 }}>
                            <TextField
                                label="To Date"
                                type="date"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: isClient ? 4 : 3 }}>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleApplyFilters}
                                    fullWidth
                                    size="large"
                                    sx={{ flex: 1 }}
                                >
                                    Apply
                                </Button>
                                <Button
                                    variant="contained"
                                    color="error"
                                    startIcon={<Print />}
                                    onClick={handlePrint}
                                    disabled={!pricingData?.consignments?.length}
                                    fullWidth
                                    size="large"
                                    sx={{ flex: 1 }}
                                >
                                    Print
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress />
                    </Box>
                ) : pricingData ? (
                    <Box>
                        {isMobile ? (
                            <Stack spacing={2}>
                                {pricingData.consignments && pricingData.consignments.length > 0 ? (
                                    pricingData.consignments.map((item: ConsignmentPricingItem) => (
                                        <Card key={item.id} variant="outlined">
                                            <CardContent>
                                                <Grid container spacing={1}>
                                                    <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Typography
                                                            component="a"
                                                            href="#"
                                                            onClick={(e) => handleConsignmentClick(e, item.id)}
                                                            sx={{
                                                                color: theme.palette.primary.main,
                                                                textDecoration: 'none',
                                                                fontWeight: 'bold',
                                                                cursor: 'pointer',
                                                                '&:hover': { textDecoration: 'underline' }
                                                            }}
                                                        >
                                                            {item.consignment_no}
                                                        </Typography>
                                                        {getStatusChip(item.pricing?.status_id || 0)}
                                                    </Grid>
                                                    <Grid size={{ xs: 12 }}>
                                                        <Typography variant="caption" color="textSecondary">
                                                            {new Date(item.created_at).toLocaleDateString('en-US', {
                                                                day: 'numeric',
                                                                month: 'short',
                                                                year: 'numeric'
                                                            })}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 12 }}>
                                                        <Divider sx={{ my: 1 }} />
                                                    </Grid>
                                                    <Grid size={{ xs: 6 }}>
                                                        <Typography variant="caption" color="textSecondary">COD Amount</Typography>
                                                        <Typography variant="body2">{item.cod_amount}</Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 6 }}>
                                                        <Typography variant="caption" color="textSecondary">Collected</Typography>
                                                        <Typography variant="body2">{item.pricing?.cod_amount || 0}</Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 6 }}>
                                                        <Typography variant="caption" color="textSecondary">Service Charges</Typography>
                                                        <Typography variant="body2">{item.pricing?.service_charges || 0}</Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 6 }}>
                                                        <Typography variant="caption" color="textSecondary">Tax</Typography>
                                                        <Typography variant="body2">{item.pricing?.tax || 0}</Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 6 }}>
                                                        <Typography variant="caption" color="textSecondary">Weight</Typography>
                                                        <Typography variant="body2">{item.pricing?.weight || 0}</Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 6 }}>
                                                        <Typography variant="caption" color="textSecondary">Basic Fee</Typography>
                                                        <Typography variant="body2">{item.pricing?.fee || 0}</Typography>
                                                    </Grid>
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    ))
                                ) : (
                                    <Typography align="center" color="textSecondary" sx={{ py: 4 }}>
                                        No records found
                                    </Typography>
                                )}
                                {pricingData.consignments && pricingData.consignments.length > 0 && (
                                    <Card variant="outlined" sx={{ bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 167, 38, 0.08)' : theme.palette.warning.light }}>
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>Total</Typography>
                                            <Grid container spacing={1}>
                                                <Grid size={{ xs: 6 }}>
                                                    <Typography variant="caption" color="textSecondary">Total COD</Typography>
                                                    <Typography variant="body2" fontWeight="bold">Rs. {totals.cod}</Typography>
                                                </Grid>
                                                <Grid size={{ xs: 6 }}>
                                                    <Typography variant="caption" color="textSecondary">Total Collected</Typography>
                                                    <Typography variant="body2" fontWeight="bold">Rs. {totals.collected}</Typography>
                                                </Grid>
                                                <Grid size={{ xs: 6 }}>
                                                    <Typography variant="caption" color="textSecondary">Total Service Charges</Typography>
                                                    <Typography variant="body2" fontWeight="bold">Rs. {totals.serviceCharges}</Typography>
                                                </Grid>
                                                <Grid size={{ xs: 6 }}>
                                                    <Typography variant="caption" color="textSecondary">Total Tax</Typography>
                                                    <Typography variant="body2" fontWeight="bold">Rs. {totals.tax}</Typography>
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                )}
                            </Stack>
                        ) : (
                            <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                                <Table>
                                    <TableHead>
                                        {pricingData.selectedUser && (
                                            <TableRow sx={{ bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 167, 38, 0.08)' : theme.palette.warning.light }}>
                                                <TableCell sx={{ fontWeight: 600 }}>User ID</TableCell>
                                                <TableCell colSpan={2}>{pricingData.selectedUser.id}</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>User Name</TableCell>
                                                <TableCell colSpan={7}>{pricingData.selectedUser.name}</TableCell>
                                            </TableRow>
                                        )}
                                        <TableRow sx={{ bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 167, 38, 0.08)' : theme.palette.warning.light }}>
                                            <TableCell sx={{ fontWeight: 600 }}>Total Consignments</TableCell>
                                            <TableCell colSpan={2}>{pricingData.consignmentsCount}</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Date Range</TableCell>
                                            <TableCell colSpan={7}>
                                                {pricingData.isDateCheck === 1 && pricingData.FromDate && pricingData.ToDate
                                                    ? `${pricingData.FromDate} - ${pricingData.ToDate}`
                                                    : 'All Time'}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow sx={{ bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 167, 38, 0.08)' : theme.palette.warning.light }}>
                                            <TableCell colSpan={11} align="center" sx={{ fontWeight: 600 }}>
                                                Consignment Pricing
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>Consignment #</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>COD Amount</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Collected Amount</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Service Charges</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Tax</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Counted Weight</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Basic Fee</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Extra KGs</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Extra Fee</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {pricingData.consignments && pricingData.consignments.length > 0 ? (
                                            <>
                                                {pricingData.consignments.map((item: ConsignmentPricingItem) => (
                                                    <TableRow key={item.id}>
                                                        <TableCell>
                                                            <Typography
                                                                component="a"
                                                                href="#"
                                                                onClick={(e) => handleConsignmentClick(e, item.id)}
                                                                sx={{
                                                                    color: theme.palette.primary.main,
                                                                    textDecoration: 'none',
                                                                    cursor: 'pointer',
                                                                    '&:hover': { textDecoration: 'underline' }
                                                                }}
                                                            >
                                                                {item.consignment_no}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            {new Date(item.created_at).toLocaleDateString('en-US', {
                                                                day: 'numeric',
                                                                month: 'short',
                                                                year: 'numeric'
                                                            })}
                                                        </TableCell>
                                                        <TableCell>
                                                            {getStatusChip(item.pricing?.status_id || 0)}
                                                        </TableCell>
                                                        <TableCell>{item.cod_amount}</TableCell>
                                                        <TableCell>{item.pricing?.cod_amount || 0}</TableCell>
                                                        <TableCell>{item.pricing?.service_charges || 0}</TableCell>
                                                        <TableCell>{item.pricing?.tax || 0}</TableCell>
                                                        <TableCell>{item.pricing?.weight || 0}</TableCell>
                                                        <TableCell>{item.pricing?.fee || 0}</TableCell>
                                                        <TableCell>{item.pricing?.multiple || 0}</TableCell>
                                                        <TableCell>{item.pricing?.extra || 0}</TableCell>
                                                    </TableRow>
                                                ))}
                                                <TableRow sx={{ bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 167, 38, 0.08)' : theme.palette.warning.light }}>
                                                    <TableCell colSpan={3} sx={{ fontWeight: 600 }}>
                                                        Total
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>Rs. {totals.cod}</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>Rs. {totals.collected}</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>Rs. {totals.serviceCharges}</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>Rs. {totals.tax}</TableCell>
                                                    <TableCell colSpan={4}></TableCell>
                                                </TableRow>
                                            </>
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={11} align="center">
                                                    No records found
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                ) : (
                    <Paper sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="body1" color="textSecondary">
                            Loading data...
                        </Typography>
                    </Paper>
                )}
            </Box>

            <ConsignmentView
                open={consignmentModalOpen}
                onClose={() => setConsignmentModalOpen(false)}
                consignmentId={selectedConsignmentId}
            />
        </Container >
    );
};

export default ConsignmentPricing;
